import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { Divider } from '@material-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectValue,
  SelectItem,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { getPropertyAddress, getPropertyUnitNumber } from '@/utils/properties';
import TextArea from '@/components/TextArea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import Typography from '../../atomic/Typography';
import useDebounce from '../../../hooks/useDebounce';
import useFetchEffect from '../../../hooks/useFetchEffect';
import { useValidation } from '../../../hooks/useValidation';
import { useActiveProperty } from '../../../store/hooks';
import {
  lookupCompanies as apiLookupCompanies,
  fetchCompany,
} from '../../../api/companies';
import { requestMissingDocumentsToJobs } from '../../../api/jobs';
import { MDRFormStyles, autocompleteStyles } from './styles';
import { compareAlphabetically } from '../../../utils/strings';
import ListItemText from '../../atomic/ListItemText';
import Autocomplete from '../../Autocomplete';
import {
  getJobYearDoneOptions,
  transformJobDoneDate,
} from '../../../utils/jobsUtils';
import { scrollToFormField } from '../../../utils/scrollToFormField';
import matomo from '../../../utils/matomo';
import Check from '../../../assets/icons/Check';
import Close from '../../../assets/icons/Close';
import Backspace from '../../../assets/icons/Backspace';
import {
  actionSetMDRInputs,
  actionSetSelectedCompany,
  actionSetJobYearDone,
  actionSetIsCompanyRegistered,
  actionSetIsCompanyInSystem,
} from '../../../store/actions/mdr';
import {
  sGetMDRInputs,
  sGetSelectedCompany,
  sGetJobYearDone,
  sGetIsCompanyRegistered,
  sGetIsCompanyInSystem,
} from '../../../store/reducers/mdr';
import { sGetOwnedProperties } from '../../../store/reducers/properties';

function MDRForm({
  classes = MDRFormStyles(),
  labels,
  ownedProperties,
  mdrInputs,
  setMDRInputs,
  setStoreSelectedCompany,
  setStoreJobYearDone,
  setStoreIsCompanyRegistered,
  setStoreIsCompanyInSystem,
  storeIsCompanyInSystem,
  storeSelectedCompany,
  storeJobYearDone,
  storeIsCompanyRegistered,
  company: passedCompany,
  companies: passedCompanies,
  ...props
}) {
  const [companyName, setCompanyName] = useState('');
  const [errors, setErrors] = useState(false);
  const debouncedCompanyName = useDebounce(companyName, 500);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { property } = useActiveProperty();
  const validatedFields = ['company'];
  const { isValid, validationData } = useValidation(
    validatedFields,
    mdrInputs || {},
    !storeIsCompanyRegistered && !storeIsCompanyInSystem,
    'companyEmail'
  );
  const inputRefs = useRef({});
  const titleRef = useRef(null);
  [...validatedFields, 'companyEmail'].forEach((field) => {
    inputRefs.current[field] = useRef(null);
  });

  // ------------------------------------------------------------------------
  // -------------------SETUP & API HANDLERS---------------------------------
  // ------------------------------------------------------------------------

  const {
    data: companies,
    setData: setCompanies,
    fetching: fetchingCompanies,
  } = useFetchEffect({
    dependencies: [debouncedCompanyName],
    apiFetchFunction: () => apiLookupCompanies({ name: debouncedCompanyName }),
    conditionFunction: ([_debouncedCompanyName]) =>
      typeof _debouncedCompanyName === 'string' &&
      _debouncedCompanyName.length > 0,
  });

  useEffect(() => {
    if (companyName === '') {
      setCompanies([]);
    }
  }, [companyName]);

  const companyOptions = (Array.isArray(companies) ? companies : [])
    .map(
      ({
        name: label,
        organizationnumber: value,
        address: companyAddress,
      }) => ({ value, label, companyAddress })
    )
    .slice(0, 10)
    .sort((a, b) => compareAlphabetically(a.label, b.label));

  const showOptionsMenu = fetchingCompanies || companyOptions.length > 0;

  // ------------------------------------------------------------------------
  // --------------------------- HANDLERS------------------------------------
  // ------------------------------------------------------------------------
  const validateSelectedCompany = async (selectedCompany) => {
    await fetchCompany(selectedCompany.value).then((res) => {
      let isRegistered = false;
      let isInSystem = true;

      if (res[1]?.companyId && res[1]?.isActive) {
        isRegistered = true;
      }

      if (res[0] || res[1]?.companyId === 0) {
        isInSystem = false;
      }
      setStoreSelectedCompany(selectedCompany);
      setStoreIsCompanyRegistered(isRegistered);
      setStoreIsCompanyInSystem(isInSystem);
    });
  };

  const onSelect = async (newSelectedCompany) => {
    if (newSelectedCompany) {
      setMDRInputs({ ...mdrInputs, company: newSelectedCompany.value });
      validateSelectedCompany(newSelectedCompany);
    }

    return null;
  };

  const onSelectYearDone = (selectedYearDone) => {
    if (selectedYearDone) {
      setStoreJobYearDone(selectedYearDone);
      setMDRInputs({
        ...mdrInputs,
        selectedJobYearDone: selectedYearDone,
      });
    }
  };

  const onPropertySelect = (selectedProperty) => {
    if (selectedProperty) {
      setMDRInputs({
        ...mdrInputs,
        selectedProperty,
      });
    }
  };

  const onCompanyNameChange = (text, { action }) => {
    const approvedActions = ['set-value', 'input-change'];
    if (approvedActions.includes(action)) {
      setCompanyName(text);
    }
  };

  const onClearCompanyName = () => {
    setCompanies([]);
    setCompanyName('');
    setStoreSelectedCompany(null);
    setStoreIsCompanyRegistered(false);
    setStoreIsCompanyInSystem(false);
    setMDRInputs({ ...mdrInputs, company: null, companyEmail: null });
  };

  const handleSendNewRequest = () => {
    onClearCompanyName();
    setStoreSelectedCompany(null);
    setStoreIsCompanyRegistered(false);
    setStoreIsCompanyInSystem(false);
    setMDRInputs({});
    setErrors(false);
    setMDRInputs({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors(true);
    const jobDate = transformJobDoneDate(storeJobYearDone);
    if (isValid) {
      const { jobDescription, companyEmail, selectedProperty } = mdrInputs;
      const data = {
        boligmappaNumber:
          selectedProperty?.boligmappaNumber || property.boligmappaNumber,
        organizationNumber: Number(storeSelectedCompany.value),
        organizationEmailAddress: companyEmail,
        description: jobDescription,
        jobDate: jobDate || '2025',
        title: `${property.boligmappaNumber}_${storeSelectedCompany.value}`,
        status: 'Pending',
        origin: 'PO',
        jobSummary: null,
        professionTypes: [],
        locations: [],
        files: [],
        tags: [],
      };

      await requestMissingDocumentsToJobs(data)
        .then((res) => {
          if (!res[1]?.success) {
            toast({
              title: `${labels.anErrorOccured} ${labels.pleaseTryAgain}`,
              variant: 'destructive',
            });
          } else {
            toast({ title: labels.requestSent, variant: 'success' });
            queryClient.invalidateQueries({ queryKey: ['sentMDRs'] });
          }
        })
        .finally(() => {
          scrollToFormField(titleRef);

          matomo.clickEvent({
            category: 'MDR Form Submission',
            name: 'MDR Form Submission',
            action: 'Form has been submitted successfully',
          });
          handleSendNewRequest();
        });
    } else {
      const name = [...validatedFields, 'companyEmail'].find(
        (el) => !validationData[el]
      );
      if (name) {
        scrollToFormField(inputRefs.current[name]);
      }
    }
  };

  const isErrorInput = (name) => {
    if (errors && validationData[name] === false) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (storeSelectedCompany) {
      setMDRInputs({ ...mdrInputs, company: storeSelectedCompany.value });
      validateSelectedCompany(storeSelectedCompany);
    }
  }, [storeSelectedCompany]);

  // ------------------------------------------------------------------------
  // -----------------------CUSTOM RENDERERS --------------------------------
  // ------------------------------------------------------------------------

  const companySearchItem = ({ label, companyAddress }) => {
    const lowerCompanyAddress = companyAddress;

    return (
      <div className={classes.companySearchItem} key={label}>
        <ListItemText>
          <Typography className={classes.companyName}>{label}</Typography>
        </ListItemText>
        {showOptionsMenu && (
          <ListItemText>
            <Typography className={classes.companyAddress}>
              {companyAddress === null || companyAddress === ''
                ? 'Adresse Ikke Tilgjengelig'
                : lowerCompanyAddress}
            </Typography>
          </ListItemText>
        )}
      </div>
    );
  };

  const renderCompanyRegisterLabel = () => {
    if (storeIsCompanyRegistered) {
      return (
        <>
          <Check className={classes.companyRegisteredIcon} />
          {labels.emailIsRegistered}
        </>
      );
    }

    if (storeIsCompanyInSystem) {
      return (
        <>
          <Close className={classes.companyRegisteredIcon} />
          {labels.emailNotRegistered}
        </>
      );
    }

    return (
      <>
        <Close className={classes.companyRegisteredIcon} />
        {labels.companyNotInSystem}
      </>
    );
  };

  const propertyDataItem = (item) => {
    const unitNumber = getPropertyUnitNumber(item);
    return (
      <>
        <span className="pr-1">{getPropertyAddress(item)}</span>
        {unitNumber && <span className="pr-1">- {unitNumber}</span>}
      </>
    );
  };
  const companySelectionSection = (
    <div ref={inputRefs.current.company} width="100%" className="relative">
      <Autocomplete
        value={storeSelectedCompany}
        onChange={onSelect}
        inputValue={companyName}
        options={companyOptions}
        escapeClearsValue={false}
        isClearable={false}
        toShrink={false}
        styles={{
          ...autocompleteStyles,
          control: (provided) => ({
            ...provided,
            border: '1px solid #e4e4e7',
            backgroundColor: '#fdfcfd',
            borderColor: isErrorInput('company')
              ? '#D7394C !important'
              : '#DDE2F1',
            boxShadow: 'none',
            '&:hover': {
              border: '1px solid #e4e4e7',
              borderSize: '1px',
              borderColor: isErrorInput('company') ? '#D7394C' : '#DDE2F1',
            },
            '&:focus': {
              border: '1px solid',
              borderColor: isErrorInput('company') ? '#D7394C' : '#DDE2F1',
            },
          }),
        }}
        className={clsx(
          classes.autocompleteInput,
          isErrorInput('company') && classes.errorInput
        )}
        menuIsOpen={showOptionsMenu}
        isLoading={fetchingCompanies}
        placeholder="Bedrift AS"
        label={labels.lblCompanyName?.toString()}
        loadingMessage={() => 'Laster...'}
        onInputChange={onCompanyNameChange}
        noOptionsMessage={() => 'Ingen selskaper'}
        formatOptionLabel={companySearchItem}
        otherComponents={{ DropdownIndicator: () => null }}
        {...props}
      />
      {(companyName || storeSelectedCompany) && (
        <>
          <Backspace
            onClick={onClearCompanyName}
            className={classes.clearTextButton}
          />
        </>
      )}
      {isErrorInput('company') && (
        <div className="text-red-500 mt-2 errorMessage errorCompany">
          {labels.errorCompany}
        </div>
      )}
      {storeSelectedCompany && (
        <div className={classes.companyRegisteredSection}>
          <Typography
            className={classes.companyRegisteredMessage}
            component="small"
            variant="inherit"
            color="textPrimary"
          >
            {renderCompanyRegisterLabel()}
          </Typography>
          <div>
            <Input
              ref={inputRefs.current.companyEmail}
              id="companyEmail"
              label={labels.lblcompanyEmail?.toString()}
              placeholder={labels.phCompanyEmail?.toString()}
              name="companyEmail"
              withCancelButton={false}
              value={mdrInputs.companyEmail}
              className={clsx([
                'h-[40px] shadow-none bg-background',
                isErrorInput('companyEmail') && 'border-red-500',
              ])}
              onChange={(e) =>
                setMDRInputs({ ...mdrInputs, companyEmail: e.target.value })
              }
            />
            {isErrorInput('companyEmail') && (
              <div className="text-red-500 mt-2 errorMessage errorCompanyEmail">
                {labels.errorCompanyEmail}
              </div>
            )}
          </div>
          <Divider />
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col p-5 gap-4 bg-card rounded-[8px] sm:text-base text-[14px]">
      <h3
        ref={titleRef}
        className="sm:pb-2 sm:text-[22px] text-[18px] font-bold"
      >
        Send forespørsel
      </h3>
      <div className="flex flex-col gap-2">
        <Label className="sm:text-base text-[14px] font-medium">
          Hvilken adresse ble jobben utført på?
        </Label>
        <Select onValueChange={(value) => onPropertySelect(value)}>
          <SelectTrigger
            defaultValue={property && property.id}
            className="h-10 bg-background"
          >
            <SelectValue placeholder={propertyDataItem(property)} />
          </SelectTrigger>
          <SelectContent>
            {ownedProperties &&
              (ownedProperties || []).map((item) => (
                <SelectItem value={item.id}>
                  {propertyDataItem(item)}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground">
          Kan kun endres hvis du har flere boliger i Boligmappa.
        </p>
      </div>
      <div className="flex flex-col gap-2">{companySelectionSection}</div>
      <div className="flex flex-col gap-2">
        <Label className="sm:text-base text-[14px] font-medium">
          Hvilket år ble jobben gjort?*
        </Label>
        <Select
          defaultValue={
            getJobYearDoneOptions(labels.moreThanFiveyears)[0].value
          }
          onValueChange={(value) => onSelectYearDone(value)}
        >
          <SelectTrigger className="h-10 bg-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {getJobYearDoneOptions(labels.moreThanFiveyears).map(
              ({ value, label }) => (
                <SelectItem value={value}>{label}</SelectItem>
              )
            )}
          </SelectContent>
        </Select>
        {storeJobYearDone === '5+' && (
          <p className="text-muted-foreground">
            {labels.moreThatFiveYearsNote}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <Label
          className="sm:text-base text-[14px] font-medium"
          htmlFor="jonDescription"
        >
          Beskrivelse av jobben
        </Label>
        <TextArea
          id="jonDescription"
          type="text"
          value={mdrInputs.jobDescription}
          onChange={(e) =>
            setMDRInputs({ ...mdrInputs, jobDescription: e.target.value })
          }
          maxRows={4}
          className="h-28 bg-background font-regular"
          placeholder="Gi en kort beskrivelse av jobben som er utført"
        />
      </div>
      <Button type="submit" onClick={handleSubmit}>
        Send forespørsel
      </Button>
    </div>
  );
}

const mapStateToProps = (state) => ({
  ownedProperties: sGetOwnedProperties(state),
  mdrInputs: sGetMDRInputs(state),
  storeSelectedCompany: sGetSelectedCompany(state),
  storeJobYearDone: sGetJobYearDone(state),
  storeIsCompanyRegistered: sGetIsCompanyRegistered(state),
  storeIsCompanyInSystem: sGetIsCompanyInSystem(state),
});

const mapDispatchToProps = (dispatch) => ({
  setMDRInputs: (inputs) => dispatch(actionSetMDRInputs(inputs)),
  setStoreSelectedCompany: (selectedCompany) =>
    dispatch(actionSetSelectedCompany(selectedCompany)),
  setStoreJobYearDone: (jobDone) => dispatch(actionSetJobYearDone(jobDone)),
  setStoreIsCompanyRegistered: (isRegistered) =>
    dispatch(actionSetIsCompanyRegistered(isRegistered)),
  setStoreIsCompanyInSystem: (isInSystem) =>
    dispatch(actionSetIsCompanyInSystem(isInSystem)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MDRForm);
